// Where a value the reader found sits on a page of the report, as the report pane draws it
// (TGCLOUD-1868, plan D1). Pure: no React, no pdf.js, no DOM.
//
// THE SERVER'S RECTS ARE IN PDF USER SPACE (plan A1): `{ x, y, w, h }`, already the box around the
// words (labReports/anchors.js): (x, y) its LOWER LEFT corner, a fifth of the font height below
// the baseline so the descenders of g, p and y are inside it, w its width and h one font height
// up from there. User space has its origin at the bottom left and y going UP. The box drawn is that
// box with a MARGIN all round, so its edge stands clear of the letters instead of running along them:
//
//     [x - MARGIN_X * h, y - MARGIN_Y * h, x + w + MARGIN_X * h, y + h + MARGIN_Y * h]
//
// It used to read (x, y) as the BASELINE and take a quarter of a font height off again: every box
// sat a quarter of a line low, its top edge along the tops of the capitals, with a gap below the
// words. Both halves were tested, each against its own reading of the contract; the test binding
// them runs the server's own anchors.js into this file.
//
// pdf.js's own viewport turns the box into CSS pixels for the page as drawn: its scale, its rotation
// and its crop box. The conversion is never done here by hand: a second copy of pdf.js's transform
// would be free to disagree with the canvas beneath it.
//
// pdf.js 6.3 has no PageViewport.convertToViewportRectangle (the plan names it; it was removed
// upstream). That method only converted the rectangle's two corners with the viewport's transform,
// and convertToViewportPoint does exactly that for one corner, so the box is its two corners
// converted by convertToViewportPoint.
//
// The page layout below (fit to width, zoom steps, canvas size, which page is on screen) is the
// pane's own arithmetic, kept here so it is tested without a browser.

/**
 * Beside the words, the box drawn reaches this fraction of the font height further out on each side,
 * past the first and last letters. Fractions of the font height, so the margin follows the size the
 * words are printed at and the zoom they are drawn at.
 */
export const MARGIN_X = 0.2;
/**
 * Above and below, a smaller margin: lines of a table cell sit close, and the box of a value that
 * wraps must not run into the letters of the line next to it.
 */
export const MARGIN_Y = 0.1;

/** The smallest and largest scale a page is drawn at (1 is the PDF's own size, 72 points an inch). */
export const MIN_SCALE = 0.25;
export const MAX_SCALE = 4;
/** One press of zoom in or zoom out multiplies or divides the scale by this. */
export const ZOOM_STEP = 1.25;
/**
 * The most canvas pixels one page is drawn with: 16,777,216 (4096 x 4096), the largest canvas area
 * Safari on iOS draws. A page that would need more at the device's pixel ratio is drawn at a lower
 * ratio instead, so it is softer rather than blank.
 */
export const MAX_CANVAS_PIXELS = 16777216;

const isNumber = (value) => typeof value === 'number' && Number.isFinite(value);

// ── One rect ───────────────────────────────────────────────────────────────────────────────────

/** A rect the pane can draw: four finite numbers, with some width and some height. */
export function isDrawableRect(rect) {
  return (
    !!rect &&
    isNumber(rect.x) &&
    isNumber(rect.y) &&
    isNumber(rect.w) &&
    isNumber(rect.h) &&
    rect.w > 0 &&
    rect.h > 0
  );
}

/**
 * The box drawn around a rect's words, its margin included, still in PDF user space:
 * [x1, y1, x2, y2].
 */
export function paddedPdfRect({ x, y, w, h }) {
  const mx = MARGIN_X * h;
  const my = MARGIN_Y * h;
  return [x - mx, y - my, x + w + mx, y + h + my];
}

/**
 * A rect as CSS pixels from the top left corner of the page as drawn: { left, top, width, height }.
 * `viewport` is the pdf.js viewport the page is drawn with (PDFPageProxy.getViewport). Its
 * transform may swap or flip the corners (y goes down on screen, and a rotated page turns them), so
 * the box is read from whichever corners come back.
 */
export function toViewportBox(viewport, rect) {
  const [px1, py1, px2, py2] = paddedPdfRect(rect);
  const [x1, y1] = viewport.convertToViewportPoint(px1, py1);
  const [x2, y2] = viewport.convertToViewportPoint(px2, py2);
  return {
    left: Math.min(x1, x2),
    top: Math.min(y1, y2),
    width: Math.abs(x2 - x1),
    height: Math.abs(y2 - y1)
  };
}

// ── The anchors of a document ──────────────────────────────────────────────────────────────────

// How an anchor asks to be drawn, kept with it: `tone` (a kind of box, such as another person's row
// of a report about several people) and `tag` (words drawn on the report beside its first box).
const drawnAs = (anchor) => ({
  ...(typeof anchor.tone === 'string' && anchor.tone
    ? { tone: anchor.tone }
    : {}),
  ...(typeof anchor.tag === 'string' && anchor.tag.trim()
    ? { tag: anchor.tag.trim() }
    : {})
});

/**
 * The anchors the pane can draw, by page: Map(page -> [{ anchorIndex, path, rects: [{ rectIndex,
 * rect }], tone?, tag? }]). `anchorIndex` is the anchor's place in `anchors`, so each box keeps one
 * key however the list is drawn. An anchor naming no page of this document, or with no drawable
 * rect, is left out: nothing is guessed about where it might be.
 *
 * @param {Array<{ path: string, page: number, rects: Array<{x, y, w, h}>, tone?: string,
 *   tag?: string }>} anchors
 * @param {number} [pageCount] the document's pages; pages past it are left out
 */
export function anchorsByPage(anchors, pageCount) {
  const byPage = new Map();
  (Array.isArray(anchors) ? anchors : []).forEach((anchor, anchorIndex) => {
    if (!anchor || typeof anchor.path !== 'string' || !anchor.path) return;
    const { page } = anchor;
    if (!Number.isInteger(page) || page < 1) return;
    if (isNumber(pageCount) && page > pageCount) return;
    const rects = (Array.isArray(anchor.rects) ? anchor.rects : [])
      .map((rect, rectIndex) => ({ rect, rectIndex }))
      .filter(({ rect }) => isDrawableRect(rect));
    if (!rects.length) return;
    if (!byPage.has(page)) byPage.set(page, []);
    byPage
      .get(page)
      .push({ anchorIndex, path: anchor.path, rects, ...drawnAs(anchor) });
  });
  return byPage;
}

/**
 * Where a path is drawn first: the first drawable rect of its first anchor on its earliest page, as
 * { page, anchorIndex, rectIndex }, or null when it is drawn nowhere.
 */
export function firstBoxOf(byPage, path) {
  const pages = [...byPage.keys()].sort((a, b) => a - b);
  for (const page of pages) {
    const anchor = byPage.get(page).find((a) => a.path === path);
    if (anchor) {
      return {
        page,
        anchorIndex: anchor.anchorIndex,
        rectIndex: anchor.rects[0].rectIndex
      };
    }
  }
  return null;
}

/** The key one box is drawn and found by: its anchor's place, then its rect's. */
export const boxKeyOf = (anchorIndex, rectIndex) =>
  `${anchorIndex}:${rectIndex}`;

/**
 * Every box drawn on one page, in reading order: top to bottom, then left to right, which is also
 * the order the keyboard reaches them in. Tops are compared to the whole pixel, so the boxes of one
 * line in two font sizes still read left to right. `first` marks each anchor's first box on the
 * page: a value printed over three lines has three boxes, and one place in the tab order. An
 * anchor's `tone` and `tag` go with each of its boxes.
 *
 * @returns {Array<{ key, anchorIndex, rectIndex, path, first, box, tone?, tag? }>}
 */
export function boxesForPage(pageAnchors, viewport) {
  const boxes = [];
  for (const anchor of pageAnchors || []) {
    anchor.rects.forEach(({ rect, rectIndex }, i) => {
      boxes.push({
        key: boxKeyOf(anchor.anchorIndex, rectIndex),
        anchorIndex: anchor.anchorIndex,
        rectIndex,
        path: anchor.path,
        first: i === 0,
        box: toViewportBox(viewport, rect),
        ...drawnAs(anchor)
      });
    });
  }
  return boxes.sort(
    (a, b) =>
      Math.round(a.box.top) - Math.round(b.box.top) ||
      a.box.left - b.box.left ||
      a.anchorIndex - b.anchorIndex ||
      a.rectIndex - b.rectIndex
  );
}

// ── Clicking ───────────────────────────────────────────────────────────────────────────────────

/** Is the point (CSS pixels from the page's top left) inside the box, edges included? */
export const boxContains = (box, point) =>
  point.x >= box.left &&
  point.x <= box.left + box.width &&
  point.y >= box.top &&
  point.y <= box.top + box.height;

/** The middle of a box: where a box pressed from the keyboard is taken to be clicked. */
export const boxCentre = (box) => ({
  x: box.left + box.width / 2,
  y: box.top + box.height / 2
});

/**
 * Every path with a box under the point, each once, in the anchors' own order. Two values read
 * from the same words (a zygosity and the words it was read from) have boxes on top of each other,
 * and a click on them is a click on both.
 */
export function pathsAtPoint(boxes, point) {
  const hits = (boxes || [])
    .filter((b) => boxContains(b.box, point))
    .sort((a, b) => a.anchorIndex - b.anchorIndex || a.rectIndex - b.rectIndex);
  return [...new Set(hits.map((b) => b.path))];
}

// ── The page layout ────────────────────────────────────────────────────────────────────────────

export const clampScale = (scale) =>
  Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));

/**
 * The scale at which the widest page fills `available` CSS pixels. 1 (the PDF's own size) when the
 * pane has no width yet or no page is known.
 */
export function fitWidthScale(available, pageWidths) {
  const widths = (Array.isArray(pageWidths) ? pageWidths : []).filter(
    (w) => isNumber(w) && w > 0
  );
  if (!(isNumber(available) && available > 0) || !widths.length) return 1;
  return clampScale(available / Math.max(...widths));
}

/** The scale a page is drawn at: the fit-to-width scale times the zoom, kept in range. */
export const zoomedScale = (fitScale, zoom) => clampScale(fitScale * zoom);

/**
 * The zoom after one press of zoom in (direction > 0) or zoom out, as a multiple of the fit scale,
 * so that the scale it gives stays inside MIN_SCALE..MAX_SCALE.
 */
export function stepZoom(zoom, fitScale, direction) {
  const factor = direction > 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
  return clampScale(fitScale * zoom * factor) / fitScale;
}

/**
 * How many canvas pixels to draw per CSS pixel: the device's pixel ratio, lowered when a page of
 * this size would need more than `maxPixels` at it.
 */
export function outputScaleFor(
  cssWidth,
  cssHeight,
  devicePixelRatio = 1,
  maxPixels = MAX_CANVAS_PIXELS
) {
  const ratio =
    isNumber(devicePixelRatio) && devicePixelRatio > 0 ? devicePixelRatio : 1;
  const area = cssWidth * cssHeight;
  if (!(isNumber(area) && area > 0)) return ratio;
  return Math.min(ratio, Math.sqrt(maxPixels / area));
}

/**
 * Each page's top, in CSS pixels from the top of the scrolled content: `gap` above the first page
 * and between each page and the next, as the pane lays them out.
 */
export function pageTops(heights, gap) {
  const tops = [];
  let y = gap;
  for (const height of heights || []) {
    tops.push(y);
    y += height + gap;
  }
  return tops;
}

/**
 * The page (counting from 1) at height `y` of the scrolled content: the page it falls on, or, in
 * the gap above a page, that page; past the last page, the last page. 0 when there are no pages.
 */
export function pageAt(tops, heights, y) {
  for (let i = 0; i < tops.length; i += 1) {
    if (y < tops[i] + heights[i]) return i + 1;
  }
  return tops.length;
}
